import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#0d1117',
    color: '#e8e5e0',
    fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    padding: '2rem',
  },
  heading: {
    fontSize: '1.75rem',
    fontWeight: 600,
    marginBottom: '1rem',
    color: '#d4943a',
  },
  message: {
    fontSize: '0.875rem',
    color: '#b0aead',
    marginBottom: '1.5rem',
    maxWidth: '480px',
    textAlign: 'center' as const,
  },
  codeBlock: {
    background: '#191c24',
    border: '1px solid #2c3040',
    borderRadius: '10px',
    padding: '1rem 1.25rem',
    fontFamily: "'IBM Plex Mono', 'Fira Code', monospace",
    fontSize: '0.8125rem',
    color: '#d45a5a',
    maxWidth: '560px',
    width: '100%',
    overflowX: 'auto' as const,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
    marginBottom: '2rem',
  },
  button: {
    background: '#d4943a',
    color: '#0d1117',
    border: 'none',
    borderRadius: '8px',
    padding: '0.625rem 1.5rem',
    fontSize: '0.9375rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    transition: 'opacity 0.15s',
  },
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <h1 style={styles.heading}>Something went wrong</h1>
          <p style={styles.message}>
            The application encountered an unexpected error. You can try
            reloading the page to recover.
          </p>
          <pre style={styles.codeBlock}>
            {this.state.error?.message ?? 'Unknown error'}
          </pre>
          <button
            type="button"
            style={styles.button}
            onClick={this.handleReload}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.opacity = '0.85';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.opacity = '1';
            }}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
